import { ChevronLeftIcon } from 'lucide-react';
import { Sheet, SheetBody, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { DrawerItem } from './DrawerItem';
import { ProgrammeList } from './ProgrammeList';
import { useWorkspace } from '../hooks/useWorkspace';
import { t } from '../../i18n';
import type { WorkspaceStore } from '../../domain/store';
import type { ViewId } from '../../types';
import type { DrawerNavItem } from '../navConfig';

export type DrawerContent = { kind: 'views'; items: readonly DrawerNavItem[]; current: ViewId } | { kind: 'programmes' };

/** Mobile hamburger drawer, built on shadcn's `Sheet` (side="left"). Its
 *  flat "views" list doubles as the whole mobile nav (no bottom tab bar) —
 *  every item switches straight to that screen, except "Programme": tapping
 *  it transitions the same sheet to the programme list instead of jumping
 *  straight into the last-edited programme, so a coach picks which one
 *  first (`onOpenProgrammeList`); `onBack` returns from that list to the
 *  flat view list. */
export function NavDrawer({
  content,
  store,
  onSelectView,
  onOpenProgrammeList,
  onSelectProgramme,
  onNewProgramme,
  onBack,
  onClose,
}: {
  content: DrawerContent | null;
  store: WorkspaceStore;
  onSelectView: (id: ViewId) => void;
  onOpenProgrammeList: () => void;
  onSelectProgramme: (id: string) => void;
  onNewProgramme: () => void;
  onBack: () => void;
  onClose: () => void;
}) {
  const workspace = useWorkspace(store);

  return (
    <Sheet
      open={content !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <SheetContent side="left" className="w-72 gap-0 p-0">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-1.5">
            {content?.kind === 'programmes' ? (
              <button
                type="button"
                onClick={onBack}
                aria-label={t('common.back')}
                className="-ml-1.5 rounded-sm p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
              >
                <ChevronLeftIcon className="size-4" />
              </button>
            ) : null}
            {content?.kind === 'programmes' ? t('programme.select') : t('app.nav')}
          </SheetTitle>
        </SheetHeader>
        <SheetBody className="flex flex-col gap-0.5 p-2">
          {content?.kind === 'views' ? (
            content.items.map((item) => (
              <DrawerItem
                key={item.id}
                icon={<item.icon className="size-5 shrink-0" />}
                label={t(item.labelKey)}
                isActive={item.id === content.current}
                onClick={() => {
                  if (item.id === 'programme') {
                    onOpenProgrammeList();
                  } else {
                    onClose();
                    onSelectView(item.id);
                  }
                }}
              />
            ))
          ) : content?.kind === 'programmes' ? (
            <ProgrammeList
              programs={workspace.programs}
              activeId={workspace.activeProgramId}
              onSelect={(id) => {
                onClose();
                onSelectProgramme(id);
              }}
              onNew={() => {
                onClose();
                onNewProgramme();
              }}
            />
          ) : null}
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}
